import * as Sentry from '@sentry/react';

/**
 * Set user context for Sentry
 * @param user - User object with id, email, and username
 */
export const setUserContext = (user: {
  id: string;
  email: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
};

/**
 * Clear user context from Sentry
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Set additional context for Sentry
 * @param context - Context object with key-value pairs
 */
export const setContext = (context: Record<string, unknown>) => {
  Object.entries(context).forEach(([key, value]) => {
    Sentry.setContext(key, value as Record<string, unknown>);
  });
};

/**
 * Add breadcrumb to Sentry
 * @param message - Breadcrumb message
 * @param category - Breadcrumb category
 * @param data - Additional data
 */
export const addBreadcrumb = (
  message: string,
  category: string = 'default',
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

/**
 * Capture exception in Sentry
 * @param error - Error object
 * @param context - Additional context
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>
) => {
  if (context) {
    setContext(context);
  }
  Sentry.captureException(error);
};

/**
 * Capture message in Sentry
 * @param message - Message to capture
 * @param level - Log level
 * @param context - Additional context
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
) => {
  if (context) {
    setContext(context);
  }
  Sentry.captureMessage(message, level);
};

/**
 * Start a performance transaction
 * Note: This function is not available in the current Sentry React version.
 * Use Sentry's automatic performance monitoring instead.
 * @param _name - Transaction name (unused)
 * @param _operation - Operation type (unused)
 */
export const startTransaction = (_name: string, _operation: string) => {
  console.warn(
    'startTransaction is not available in the current Sentry React version. Use automatic performance monitoring instead.'
  );
  return null;
};

/**
 * Set tag for Sentry
 * @param key - Tag key
 * @param value - Tag value
 */
export const setTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Set multiple tags for Sentry
 * @param tags - Object with tag key-value pairs
 */
export const setTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
};
